import styled from "@emotion/styled";
import { useCallback } from "react";

import { Button, FONT_WEIGHT_SEMI_BOLD, toast } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { ValueReferenceCycle } from "@skilitics/nautilus-core-designer-data";

import { useFollowNodeLink } from "../../navigation/useFollowNodeLink.ts";
import type { NodeLink } from "../../navigation/useGetNodeLink.ts";
import { useSummariseValueReferenceCycle, type ValueReferenceCycleSummary } from "./useSummariseValueReferenceCycle.ts";

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin: 15px 0 0;
`;

const Cycle = styled.div`
  & + & {
    margin-top: 8px;
  }
`;

const Chain = styled.div`
  margin-bottom: 2px;
`;

const NodeNameLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  color: inherit;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
`;

const NodeName = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

interface Props {
  message: string;
  /** Formatted by the caller: this renders outside the locale provider, as `message` does. */
  dismissLabel: string;
  cycles: readonly ValueReferenceCycleSummary[];
  onFollowNodeLink: (link: NodeLink) => void;
  onDismiss: () => void;
}

export const ValueReferenceCyclesBlockedToast = ({
  message,
  dismissLabel,
  cycles,
  onFollowNodeLink,
  onDismiss,
}: Props) => (
  <div data-test="value-reference-cycles-toast">
    <p>{message}</p>
    {cycles.map((cycle) => (
      <Cycle key={cycle.key}>
        <Chain>
          {cycle.nodes.map(({ title, link }, index) => (
            <span key={`${title}-${index}`}>
              {index > 0 && " → "}
              {link === undefined ? (
                <NodeName>{title}</NodeName>
              ) : (
                <NodeNameLink
                  type="button"
                  onClick={() => onFollowNodeLink(link)}
                  data-test="value-reference-cycle-node"
                >
                  {title}
                </NodeNameLink>
              )}
            </span>
          ))}
        </Chain>
        <div>{cycle.fixAdvice}</div>
      </Cycle>
    ))}
    <ButtonContainer>
      <Button onClick={onDismiss} color="inherit" data-test="value-reference-cycles-toast-dismiss">
        {dismissLabel}
      </Button>
    </ButtonContainer>
  </div>
);

/**
 * Report the loops that stopped a publish or a preview, each name a way to get to it. Stays until
 * dismissed — the author has to go and break a loop, and a toast that expires on its own takes the
 * list away with it — so following a name keeps it up for the rest of the loop.
 */
export const useShowValueReferenceCyclesBlockedToast = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const summariseValueReferenceCycle = useSummariseValueReferenceCycle();
  const followNodeLink = useFollowNodeLink();

  return useCallback(
    (cycles: readonly ValueReferenceCycle[], action: "publish" | "preview") => {
      // Resolved here, where the store and router are; the toast renders outside both.
      const summaries = cycles.map(summariseValueReferenceCycle);
      toast.error(
        (t) => (
          <ValueReferenceCyclesBlockedToast
            message={formatMessage(action === "publish" ? "publishBlocked" : "previewBlocked")}
            dismissLabel={formatMessage("dismiss")}
            cycles={summaries}
            onFollowNodeLink={followNodeLink}
            onDismiss={() => toast.dismiss(t.id)}
          />
        ),
        { duration: Infinity, position: "top-center", id: "value-reference-cycles-blocked" },
      );
    },
    [formatMessage, followNodeLink, summariseValueReferenceCycle],
  );
};

const formatStrings = {
  publishBlocked: {
    en: "Publishing was blocked: these values depend on each other in a loop. Break the loops and publish again:",
  },
  previewBlocked: {
    en: "Previewing was blocked: these values depend on each other in a loop. Break the loops and preview again:",
  },
  dismiss: {
    en: "Dismiss",
  },
} satisfies FormatMessages;
